import type { ResponseBreakdown } from "../api";

type Props = {
  responses: ResponseBreakdown[];
  optionLabelMap: Record<string, string>;
  denominator: "answered" | "started";
  isMulti: boolean;
  kind: "options" | "free_text";
};

function fmtPct(p: number): string {
  return `${p.toFixed(2)}%`;
}

function fmtRank(idx: number): string {
  return String(idx + 1).padStart(2, "0");
}

export function ResponseBreakdownRow({
  responses,
  optionLabelMap,
  denominator,
  isMulti,
  kind,
}: Props) {
  if (kind === "free_text") {
    return (
      <div className="px-6 py-5 text-sm text-mars-white/60">
        Free-text response — see Submissions tab.
      </div>
    );
  }

  // Hide responses tied to option ids that no longer exist in the current
  // quiz definition. Stale ids would otherwise add noise (e.g. <1% rows from
  // an option that was removed mid-experiment).
  const visible = responses.filter((r) => optionLabelMap[r.answer_id] !== undefined);

  if (visible.length === 0) {
    return (
      <div className="px-6 py-5 text-sm text-mars-white/60">
        No answers in range.
      </div>
    );
  }

  return (
    <div className="space-y-4 px-6 py-5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <div className="eyebrow text-mars-white/55">
          Response breakdown
        </div>
        {isMulti && (
          <div className="mono text-[0.6rem] uppercase tracking-[0.18em] text-mars-orange/80">
            Multi-select · totals may exceed 100%
          </div>
        )}
      </div>
      <div className="space-y-2">
        {visible.map((r, i) => {
          const label = optionLabelMap[r.answer_id] ?? r.answer_id;
          const pct =
            denominator === "answered" ? r.pct_of_answered : r.pct_of_started;
          const isLeader = i === 0;
          return (
            <div
              key={r.answer_id}
              className="grid grid-cols-[2.25rem_minmax(8rem,16rem)_1fr_7.5rem] items-center gap-3 text-sm"
            >
              <div
                className={
                  isLeader
                    ? "mono text-[0.7rem] font-bold tabular-nums text-mars-orange"
                    : "mono text-[0.7rem] tabular-nums text-mars-white/35"
                }
              >
                {fmtRank(i)}
              </div>
              <div
                className={
                  isLeader
                    ? "truncate font-medium text-mars-white"
                    : "truncate text-mars-white/80"
                }
                title={label}
              >
                {label}
              </div>
              <div className="relative h-2.5 overflow-hidden rounded-full bg-mars-line/40">
                <div
                  className={
                    isLeader
                      ? "absolute inset-y-0 left-0 rounded-full bg-mars-orange"
                      : "absolute inset-y-0 left-0 rounded-full bg-mars-orange/50"
                  }
                  style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                  data-testid={`bar-${r.answer_id}`}
                />
              </div>
              <div className="mono flex items-baseline justify-end gap-2 tabular-nums">
                <span
                  className={
                    isLeader
                      ? "text-base font-bold text-mars-white"
                      : "text-sm text-mars-white/85"
                  }
                >
                  {fmtPct(pct)}
                </span>
                <span className="text-[0.65rem] uppercase tracking-[0.12em] text-mars-white/40">
                  {r.count.toLocaleString("en-US")}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
